import React, { useCallback, useContext } from 'react';
import classnames from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import noop from '../../../../utils/noop';
import { getCommentableTypeForDeviationType } from '../../../../DeviationPage/redux/helpers';
import { useSubscriptionTier } from '../../../../utils/hooks/useSubscriptionTier';
import Stats from '../../../../Stats';
import useFocusOnCommentBox from '../useFocusOnCommentBox';
import PriceAndSubscribeButton from '../PriceAndSubscribeButton';
import FaveControl from './FaveControl';
import ShareControl from './ShareControl';
import CommentControl from './CommentControl';

import s from './FeedCardActionBar.scss';

export interface Props {
  deviation: PapiDeviation;
  withFaveControl?: boolean;
  withCommentControl?: boolean;
  className?: string;
  additionalActions?: React.ReactNode;
  openCommentThread?: () => void;
}

const FeedCardActionBar: React.FC<Props> = ({
  deviation,
  withFaveControl,
  withCommentControl,
  className,
  additionalActions,
  openCommentThread = noop,
}) => {
  const isMobile = useContext(MobileContext);
  const focusOnTopCommentBox = useFocusOnCommentBox(
    getCommentableTypeForDeviationType(deviation),
    deviation.deviationId,
    'topmost',
    false
  );
  const openCommentThreadAndFocus = useCallback(() => {
    openCommentThread();
    focusOnTopCommentBox();
  }, [openCommentThread, focusOnTopCommentBox]);

  const { tier, needsSubscription } = useSubscriptionTier(deviation);
  const shouldRenderSubscribe = isMobile && !!tier && needsSubscription;
  return (
    <div className={classnames(s['root'], className)}>
      <div className={s['actions']}>
        {withFaveControl && (
          <FaveControl
            className={s['action']}
            iconClassName={s['action-icon']}
            textClassName={s['action-text']}
            deviation={deviation}
          />
        )}
        {withCommentControl && (
          <CommentControl
            className={s['action']}
            iconClassName={s['action-icon']}
            textClassName={s['action-text']}
            disabledClassName={s['disabled']}
            deviation={deviation}
            onClick={openCommentThreadAndFocus}
          />
        )}
        <ShareControl
          buttonClassName={s['share-button']}
          className={s['action']}
          iconClassName={s['action-icon']}
          textClassName={s['action-text']}
          deviation={deviation}
        />
        {shouldRenderSubscribe && (
          <PriceAndSubscribeButton className={s['action']} tier={tier!} />
        )}
      </div>

      {!isMobile && (
        <Stats
          className={s['stats']}
          deviation={deviation}
          onlyNumbers
          withFavouriteControl={withFaveControl}
          withCommentControl={withCommentControl}
          statClassName={s['stat']}
          withViewCount={false}
          withBadges={false}
          onCommentClick={openCommentThread}
        />
      )}

      {additionalActions}
    </div>
  );
};
FeedCardActionBar.displayName = 'FeedCardActionBar';

export default FeedCardActionBar;
